// src/store/apis/invoiceApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import stockApi from "./stockApi";

/* ------------------------------ helpers ------------------------------ */
const key = (x) => String(x ?? "");
const sameId = (a, b) => key(a) === key(b);
const sameDoc = (it, id) => sameId(it?._id ?? it?.id, id);

const pickData = (res) => res?.data ?? res?.entry ?? res;

const toNum = (v) => Number(v ?? 0) || 0;
const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;
const round3 = (x) => Math.round((toNum(x) + Number.EPSILON) * 1000) / 1000;

const normLimit = (args, draft) =>
  Number(args?.limit ?? draft?.pagination?.limit ?? draft?.limit ?? 10) || 10;

/* ----------------------------- totals utils ----------------------------- */
const incTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs += by;
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(
      1,
      Math.ceil(draft.pagination.totalDocs / lim),
    );
  } else if (draft?.total != null) {
    draft.total += by;
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

const decTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs = Math.max(0, draft.pagination.totalDocs - by);
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(
      1,
      Math.ceil(draft.pagination.totalDocs / lim),
    );
  } else if (draft?.total != null) {
    draft.total = Math.max(0, draft.total - by);
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

/* --------------------------- list manipulation --------------------------- */
const addIntoListDraft = (draft, args, newItems = []) => {
  if (!Array.isArray(draft?.rows)) draft.rows = [];
  if (!newItems?.length) return;

  // if user is on page 2+, don't inject rows (only totals)
  if (args.page && Number(args.page) !== 1) {
    incTotals(draft, newItems.length);
    return;
  }

  draft.rows.unshift(...newItems);

  const lim = normLimit(args, draft);
  if (lim > 0 && draft.rows.length > lim) {
    draft.rows.splice(lim);
  }
  incTotals(draft, newItems.length);
};

/* --------------------------- filter matching --------------------------- */
const matchesInvFilters = (inv = {}, args = {}) => {
  const {
    branchId = "",
    customerId = "",
    from = "",
    to = "",
    search = "", // client side only
  } = args || {};

  const rowBranchId = inv?.branchId?._id ?? inv?.branchId;
  const rowCustomerId = inv?.customerId?._id ?? inv?.customerId;

  if (branchId && !sameId(rowBranchId, branchId)) return false;
  if (customerId && !sameId(rowCustomerId, customerId)) return false;

  if (from || to) {
    const d = new Date(inv.invDate || inv.date || inv.createdAt);
    if (from && d < new Date(from)) return false;
    if (to && d > new Date(to)) return false;
  }

  if (search) {
    const hay = [
      String(inv?.invNo ?? ""),
      inv?.invCode,
      inv?.notes,
      inv?.customerId?.customerName,
      inv?.customerId?.customerId,
      inv?.branchId?.branchName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(search.toLowerCase())) return false;
  }

  return true;
};

/* ----------------------- compute totals (client) ----------------------- */
const computeInvoiceTotals = (
  stockEntries = [],
  looseStones = [],
  looseDiamonds = [],
  opts = {},
) => {
  const discount = toNum(opts?.totalDiscount);
  const courier = toNum(opts?.courierCharge);
  const gstRoundOff = !!opts?.gstRoundOff;

  const t = {
    items: 0,
    qty: 0,
    totalWt: 0,
    packingWt: 0,
    grossWt: 0,
    stoneWt: 0,
    diamondWt: 0,
    netWt: 0,
    stonePcs: 0,
    diamondPcs: 0,

    subTotal: 0,
    gstTotal: 0,
    lineGrand: 0,

    discount: round2(discount),
    courier: round2(courier),
    roundOff: 0,

    grandTotal: 0,
    paid: 0,
    outstanding: 0,
  };

  for (const it of stockEntries || []) {
    t.items += 1;
    t.qty += toNum(it?.qty);
    t.totalWt += toNum(it?.totalWt);
    t.packingWt += toNum(it?.packingWt);
    t.grossWt += toNum(it?.grossWt);
    t.stoneWt += toNum(it?.stoneWt);
    t.diamondWt += toNum(it?.diamondWt);
    t.netWt += toNum(it?.netWt);
    t.stonePcs += toNum(it?.stonePcs);
    t.diamondPcs += toNum(it?.diamondPcs);
    t.subTotal += toNum(it?.itemAmount);
    t.gstTotal += toNum(it?.itemGstAmount);
    t.lineGrand += toNum(it?.totalItemAmount);
  }
  for (const s of looseStones || []) {
    t.items += 1;
    t.subTotal += toNum(s?.itemAmount);
    t.gstTotal += toNum(s?.itemGstAmount);
    t.lineGrand += toNum(s?.totalItemAmount);
  }
  for (const d of looseDiamonds || []) {
    t.items += 1;
    t.subTotal += toNum(d?.itemAmount);
    t.gstTotal += toNum(d?.itemGstAmount);
    t.lineGrand += toNum(d?.totalItemAmount);
  }

  const gross = t.lineGrand - t.discount + t.courier;
  const rounded = Math.round(gross);
  t.roundOff = gstRoundOff ? round2(rounded - gross) : 0;
  t.grandTotal = round2(gross + t.roundOff);

  t.totalWt = round3(t.totalWt);
  t.packingWt = round3(t.packingWt);
  t.grossWt = round3(t.grossWt);
  t.stoneWt = round3(t.stoneWt);
  t.diamondWt = round3(t.diamondWt);
  t.netWt = round3(t.netWt);

  return t;
};

/* ------------------- default args + cache seed/upsert ------------------- */
const defaultListArgs = (fromArgs = {}) => ({
  page: Number(fromArgs.page) || 1,
  limit: Number(fromArgs.limit) || 10,
  branchId: fromArgs.branchId || "",
  customerId: fromArgs.customerId || "",
  from: fromArgs.from || "",
  to: fromArgs.to || "",
  search: fromArgs.search || "",
  sortBy: fromArgs.sortBy,
  sortOrder: fromArgs.sortOrder,
});

const ensureListCacheAndInject = (dispatch, getState, args, rows) => {
  const a = defaultListArgs(args);
  const sel = invoiceApi.endpoints.getInvoices.select(a)(getState());
  if (sel?.data) {
    dispatch(
      invoiceApi.util.updateQueryData("getInvoices", a, (draft) => {
        addIntoListDraft(draft, a, rows);
      }),
    );
  } else {
    const lim = normLimit(a);
    const draft = { rows: [], total: 0, page: a.page, limit: lim };
    addIntoListDraft(draft, a, rows);
    dispatch(invoiceApi.util.upsertQueryData("getInvoices", a, draft));
  }
};

const upsertOrPatchRow = (dispatch, getState, args, updatedRow) => {
  const a = defaultListArgs(args);
  const sel = invoiceApi.endpoints.getInvoices.select(a)(getState());
  if (sel?.data) {
    dispatch(
      invoiceApi.util.updateQueryData("getInvoices", a, (draft) => {
        if (!Array.isArray(draft?.rows)) draft.rows = [];
        const idx = draft.rows.findIndex((it) => sameDoc(it, updatedRow?._id));
        if (idx >= 0) {
          draft.rows[idx] = { ...draft.rows[idx], ...updatedRow };
        } else if (matchesInvFilters(updatedRow, a)) {
          addIntoListDraft(draft, a, [updatedRow]);
        }
      }),
    );
  } else if (matchesInvFilters(updatedRow, a)) {
    const lim = normLimit(a);
    dispatch(
      invoiceApi.util.upsertQueryData("getInvoices", a, {
        rows: [updatedRow].slice(0, lim),
        total: 1,
        page: a.page,
        limit: lim,
      }),
    );
  }
};

/* -------------------- build optimistic row from form -------------------- */
const buildTempInvoiceRow = (payload, tempId) => {
  const nowIso = new Date().toISOString();

  const stockEntries = Array.isArray(payload?.stockEntries)
    ? payload.stockEntries.map((r) => ({
        qty: toNum(r?.qty),
        totalWt: toNum(r?.totalWt),
        packingWt: toNum(r?.packingWt),
        grossWt: toNum(r?.grossWt),
        stoneWt: toNum(r?.stoneWt),
        diamondWt: toNum(r?.diamondWt),
        netWt: toNum(r?.netWt),
        stonePcs: toNum(r?.stonePcs),
        diamondPcs: toNum(r?.diamondPcs),
        itemAmount: toNum(r?.itemAmount),
        itemGstAmount: toNum(r?.itemGstAmount),
        totalItemAmount: toNum(r?.totalItemAmount),

        itemCode: r?.itemCode?.label || "",
        categoryName: r?.category?.label || "",
        productName: r?.product?.label || "",
        designName: r?.design?.label || "",
        purityName: r?.purity?.label || "",
      }))
    : [];

  const looseStones = Array.isArray(payload?.looseStones)
    ? payload.looseStones.map((s) => ({
        itemAmount: toNum(s?.itemAmount),
        itemGstAmount: toNum(s?.itemGstAmount),
        totalItemAmount: toNum(s?.totalItemAmount),
        stoneName:
          s?.stnPopupName?.label || s?.stnPopupName?.info?.stoneName || "",
        unit: s?.unit || s?.stnPopupName?.info?.unit || "",
        wt: toNum(s?.stnPopupWt),
        pcs: toNum(s?.stnPopupPcs),
      }))
    : [];

  const looseDiamonds = Array.isArray(payload?.looseDiamonds)
    ? payload.looseDiamonds.map((d) => ({
        itemAmount: toNum(d?.itemAmount),
        itemGstAmount: toNum(d?.itemGstAmount),
        totalItemAmount: toNum(d?.totalItemAmount),
        diamondName: d?.diaName?.label || d?.diaName?.info?.diamondName || "",
        unit: "carat",
        ct: toNum(d?.diaWt),
        pcs: toNum(d?.pcs),
      }))
    : [];

  const payments = Array.isArray(payload?.payments)
    ? payload.payments.map((p) => ({
        type: "Receipt",
        mode: p?.mode || "Cash",
        amount: toNum(p?.amount),
      }))
    : [];

  const totals = computeInvoiceTotals(
    stockEntries,
    looseStones,
    looseDiamonds,
    {
      totalDiscount: payload?.totalDiscount,
      courierCharge: payload?.courierCharge,
      gstRoundOff: payload?.gstRoundOff,
    },
  );

  totals.paid = round2(payments.reduce((s, p) => s + toNum(p?.amount), 0));
  totals.outstanding = round2(totals.grandTotal - totals.paid);

  const branch = payload?.branch;
  const customer = payload?.customer;

  return {
    _id: tempId,
    invNo: toNum(payload?.invoiceNo) || undefined,
    invCode: payload?.invCode || undefined,
    invDate: payload?.invoiceDate
      ? new Date(payload.invoiceDate).toISOString()
      : nowIso,
    notes: payload?.notes || "",

    branchId: branch
      ? { _id: branch.value, branchName: branch.label }
      : payload?.branchId
        ? { _id: payload.branchId }
        : undefined,

    customerId: customer
      ? {
          _id: customer.value,
          customerName: customer?.info?.customerName || customer?.label || "",
          customerId: customer?.info?.customerId || customer?.label || "",
          balanceAmount: customer?.info?.balanceAmount,
        }
      : payload?.customerId
        ? { _id: payload.customerId }
        : undefined,

    stockEntries,
    looseStones,
    looseDiamonds,
    totals,
    payments,

    isActive: true,
    __temp: true,
  };
};

/* --------------------------- export param builder --------------------------- */
const buildInvoiceParams = (args = {}) => {
  const {
    page,
    limit,
    branchId = "",
    customerId = "",
    from = "",
    to = "",
    sortBy,
    sortOrder,
  } = args || {};

  const params = {};
  if (page != null) params.page = page;
  if (limit != null) params.limit = limit;

  if (branchId) params.branchId = branchId;
  if (customerId) params.customerId = customerId;
  if (from) params.from = from;
  if (to) params.to = to;
  if (sortBy) params.sortBy = sortBy;
  if (sortOrder) params.sortOrder = sortOrder;

  return params;
};

/* --------------------------------------------------------------------- */

export const invoiceApi = createApi({
  reducerPath: "invoiceApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
tagTypes: ["INV", "INV_LAST_NO", "GST_REPORT"],
  endpoints: (builder) => ({
    /* ------------------------------ LIST ------------------------------ */
    getInvoices: builder.query({
      query: (args = {}) => {
        const { page = 1, limit = 10 } = args;
        const params = buildInvoiceParams({ ...args, page, limit });
        return { url: "/invoices", method: "GET", params };
      },
      providesTags: ["INV"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        // note: `search` only for client-side, but we keep it in cache key
        return [
          endpointName,
          a.page,
          a.limit,
          a.branchId,
          a.customerId,
          a.from,
          a.to,
          a.search,
          a.sortBy,
          a.sortOrder,
        ].join("|");
      },
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),

    /* ----------------------------- GET ONE --------------------------- */
    getInvoiceById: builder.query({
      query: (id) => `/invoices/${id}`,
      providesTags: (_res, _err, id) => [{ type: "INV", id: key(id) }],
    }),

    /* ----------------------------- CREATE ---------------------------- */
    addInvoice: builder.mutation({
      query: (payload) => ({
        url: "/invoices",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        const tempId = `tmp_inv_${Date.now()}`;
        const tempRow = buildTempInvoiceRow(payload, tempId);

        const undoPatches = [];
        const allArgs = invoiceApi.util.selectCachedArgsForQuery(
          getState(),
          "getInvoices",
        );
        for (const args of allArgs) {
          if (!matchesInvFilters(tempRow, args)) continue;
          undoPatches.push(
            dispatch(
              invoiceApi.util.updateQueryData("getInvoices", args, (draft) => {
                addIntoListDraft(draft, args, [tempRow]);
              }),
            ),
          );
        }
        ensureListCacheAndInject(dispatch, getState, { page: 1, limit: 10 }, [
          tempRow,
        ]);

        try {
          const { data: res } = await queryFulfilled;
          const created = pickData(res);
          const real = Array.isArray(created) ? created[0] : created;
          if (!real) return;
        
          dispatch(stockApi.util.invalidateTags(["LabelledItem"]));
          const again = invoiceApi.util.selectCachedArgsForQuery(
            getState(),
            "getInvoices",
          );
          for (const args of again) {
            dispatch(
              invoiceApi.util.updateQueryData("getInvoices", args, (draft) => {
                if (!Array.isArray(draft?.rows)) return;
                const pos = draft.rows.findIndex((r) => sameDoc(r, tempId));
                const nowMatches = matchesInvFilters(real, args);

                if (pos >= 0) {
                  if (nowMatches) {
                    draft.rows[pos] = { ...draft.rows[pos], ...real };
                    delete draft.rows[pos].__temp;
                  } else {
                    draft.rows.splice(pos, 1);
                    decTotals(draft, 1);
                  }
                } else if (nowMatches) {
                  addIntoListDraft(draft, args, [real]);
                }
              }),
            );
          }
        } catch {
          undoPatches.forEach((p) => p?.undo && p.undo());
        }
      },
      invalidatesTags: ["INV", "INV_LAST_NO"],
    }),

    /* ----------------------------- UPDATE ---------------------------- */
    updateInvoice: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/invoices/${id}`,
        method: "PUT",
        body: patch,
      }),

      // 🔥 bas ye kaafi hai
      invalidatesTags: (result, error, { id }) => [
        "INV", // list refetch
        { type: "INV", id }, // detail refetch
      ],
    }),

    /* ----------------------------- DELETE ---------------------------- */
   deleteInvoice: builder.mutation({
  query: ({ id, deleteReason }) => ({
    url: `/invoices/${id}/delete`,
    method: "POST",
    body: { deleteReason },
  }),
  async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
    const patches = [];
    const allArgs = invoiceApi.util.selectCachedArgsForQuery(
      getState(),
      "getInvoices"
    );

    for (const args of allArgs) {
      patches.push(
        dispatch(
          invoiceApi.util.updateQueryData("getInvoices", args, (draft) => {
            if (!Array.isArray(draft?.rows)) return;
            const before = draft.rows.length;
            draft.rows = draft.rows.filter((it) => !sameDoc(it, id));
            const removed = before - draft.rows.length;
            if (removed > 0) decTotals(draft, removed);
          })
        )
      );
    }

    try {
      await queryFulfilled;
    } catch {
      patches.forEach((p) => p?.undo && p.undo());
    }
  },
  invalidatesTags: ["INV_LAST_NO"],
}),

/* ------------------------ DELETED LIST ------------------------ */
getDeletedInvoices: builder.query({
  query: (args = {}) => {
    const {
      page = 1,
      limit = 20,
      branchId = "",
      customerId = "",
      from = "",
      to = "",
      deletedFrom = "",
      deletedTo = "",
    } = args;

    const params = {
      page,
      limit,
    };

    if (branchId) params.branchId = branchId;
    if (customerId) params.customerId = customerId;
    if (from) params.from = from;
    if (to) params.to = to;
    if (deletedFrom) params.deletedFrom = deletedFrom;
    if (deletedTo) params.deletedTo = deletedTo;

    return {
      url: "/invoices/deleted",
      method: "GET",
      params,
    };
  },
  providesTags: ["INV_DELETED"],
  serializeQueryArgs: ({ endpointName, queryArgs }) => {
    const a = queryArgs || {};
    return [
      endpointName,
      a.page,
      a.limit,
      a.branchId,
      a.customerId,
      a.from,
      a.to,
      a.deletedFrom,
      a.deletedTo,
    ].join("|");
  },
  keepUnusedDataFor: 60,
}),


    /* ------------------------- LAST INV NUMBER ------------------------ */
    getLastInvoiceNumber: builder.query({
      query: (args = {}) => {
        const { branchId = "", date = "" } = args || {};
        const params = {};
        if (branchId) params.branchId = branchId;
        if (date) params.date = date;
        return { url: "/invoices/last-number", method: "GET", params };
      },
      providesTags: ["INV_LAST_NO"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [endpointName, a.branchId || "", a.date || ""].join("|");
      },
      keepUnusedDataFor: 30,
      refetchOnMountOrArgChange: true,
    }),

    /* -------------------------- SEND SMS -------------------------- */
    sendInvoiceSms: builder.mutation({
      query: (id) => ({
        url: `/invoices/${id}/send-sms`,
        method: "POST",
      }),
    }),

    /* -------------------------- RECEIPT PDF (BLOB) -------------------------- */
    downloadInvoiceReceiptPdf: builder.mutation({
      query: (id) => ({
        url: `/invoices/${id}/receipt.pdf`,
        method: "GET",
        // IMPORTANT: return blob
        responseHandler: (response) => response.text(),
      }),
    }),

    /* -------------------------- EXPORT EXCEL (BLOB) -------------------------- */
    exportInvoicesExcel: builder.mutation({
      query: (args = {}) => ({
        url: `/invoices/export/excel`,
        method: "GET",
        params: buildInvoiceParams(args),
        responseHandler: (response) => response.blob(),
      }),
    }),

    /* -------------------------- EXPORT PDF ZIP (BLOB) -------------------------- */
    exportInvoicesPdfZip: builder.mutation({
      query: (args = {}) => ({
        url: `/invoices/export/pdfs`,
        method: "GET",
        params: buildInvoiceParams(args),
        responseHandler: (response) => response.blob(),
      }),
    }),

    /* -------------------------- GST REPORT -------------------------- */
getGstReport: builder.query({
  query: (args = {}) => {
    const {
      branchId = "",
      fromDate = "",
      toDate = "",
      month = "",
      year = "",
    } = args;

    const params = {};

    if (branchId) params.branchId = branchId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (month) params.month = month;
    if (year) params.year = year;

    return {
      url: "/invoices/gst-report",
      method: "GET",
      params,
    };
  },
  providesTags: ["GST_REPORT"],
  serializeQueryArgs: ({ endpointName, queryArgs }) => {
    const a = queryArgs || {};
    return [
      endpointName,
      a.branchId,
      a.fromDate,
      a.toDate,
      a.month,
      a.year,
    ].join("|");
  },
  keepUnusedDataFor: 60,
}),

/* -------------------------- GST REPORT DOWNLOAD (EXCEL) -------------------------- */
downloadGstReport: builder.mutation({
  query: (args = {}) => {
    const {
      branchId = "",
      fromDate = "",
      toDate = "",
      month = "",
      year = "",
    } = args;

    const params = {};

    if (branchId) params.branchId = branchId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (month) params.month = month;
    if (year) params.year = year;

    return {
      url: "/invoices/gst-report/download",
      method: "GET",
      params,
      responseHandler: (response) => response.blob(), // 🔥 important
    };
  },
}),


/* -------------------------- SALES RETURN GST REPORT -------------------------- */
getSalesReturnGstReport: builder.query({
  query: (args = {}) => {
    const {
      branchId = "",
      fromDate = "",
      toDate = "",
      month = "",
      year = "",
      srPage = 1,
      srLimit = 10,
      itemPage = 1,
      itemLimit = 10,
    } = args;

    const params = {};

    if (branchId) params.branchId = branchId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (month) params.month = month;
    if (year) params.year = year;

    params.srPage = srPage;
    params.srLimit = srLimit;
    params.itemPage = itemPage;
    params.itemLimit = itemLimit;

    return {
      url: "/sales-return/gst-report",
      method: "GET",
      params,
    };
  },
  providesTags: ["SR_GST_REPORT"],
  serializeQueryArgs: ({ endpointName, queryArgs }) => {
    const a = queryArgs || {};
    return [
      endpointName,
      a.branchId || "",
      a.fromDate || "",
      a.toDate || "",
      a.month || "",
      a.year || "",
      a.srPage || 1,
      a.srLimit || 10,
      a.itemPage || 1,
      a.itemLimit || 10,
    ].join("|");
  },
  keepUnusedDataFor: 60,
}),

/* --------------------- SALES RETURN GST REPORT DOWNLOAD --------------------- */
downloadSalesReturnGstReport: builder.mutation({
  query: (args = {}) => {
    const {
      branchId = "",
      fromDate = "",
      toDate = "",
      month = "",
      year = "",
    } = args;

    const params = {};

    if (branchId) params.branchId = branchId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (month) params.month = month;
    if (year) params.year = year;

    return {
      url: "/sales-return/gst-report/download",
      method: "GET",
      params,
      responseHandler: (response) => response.blob(),
    };
  },
}),

  }),
});

/* ----------------------------- hooks ----------------------------- */
export const {
  useGetInvoicesQuery,
  useGetInvoiceByIdQuery,
  useAddInvoiceMutation,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
  useGetLastInvoiceNumberQuery,
  useSendInvoiceSmsMutation,
  useGetDeletedInvoicesQuery,   // 👈 ADD HERE
useGetGstReportQuery,
  // downloads/exports
  useDownloadGstReportMutation,
  useDownloadInvoiceReceiptPdfMutation,
  useExportInvoicesExcelMutation,
  useExportInvoicesPdfZipMutation,

  useGetSalesReturnGstReportQuery,
useDownloadSalesReturnGstReportMutation,
} = invoiceApi;

export default invoiceApi;
